/* The card's "Linked counterparties" block — the Overview tab's last part, in both VIEW and EDIT.

   Read from the card itself (`linked_counterparties` on `GET excalibur/counterparties/:id/`,
   already sorted by name, soft-deleted ones left out). Written through its own two endpoints,
   neither of which is the card's PATCH:
     POST   …/:id/links/            {counterparty, link_type | null} → the full card
     DELETE …/:id/links/:other_id/                                   → the full card
   so linking never waits for Save and never touches the form. Every answer is handed up as the
   new LIST only (`onChange`): the parent merges it into the shown record and its pristine
   baseline alike, which is what keeps unsaved edits, a staged photo and the dirty check intact
   while the form is open.

   ⚠ Success is "the answer has an `id`", NOT `isOk()`. A link that is already gone answers 404
   with a JSON `{detail}` body, and `call()` hands any parsed JSON to the callback as data — so
   `isOk()` would score that 404 a success and the card would be replaced by `{detail}`.

   A table, as the card's Orders tab and `/supervisors/:id`'s tabs are, and a confirm that names
   the counterparty (`supervisor/Page.js`). ⚠ Unlink is a direct icon button, NOT the app-wide
   row-action menu — a deliberate exception, asked for: unlinking is the only thing a row here can
   do, so a menu holding one item was an extra click in front of it. What keeps it safe is the
   confirm, not the menu. (`task/Answers.js`' inline verdict buttons are the other exception.) The
   picker is the same modal-with-`EntitySelect` shape as `supervisor/Page.js`. The API, not the form, refuses a
   duplicate or the card itself; its 400 marks the field with the card form's own phrase. */

import React, { useState } from "react";
import { Link } from "react-router-dom";
import { Avatar, Button, Empty, Flex, Form, Modal, Table, Tag, Tooltip, Typography } from "antd";
import { DisconnectOutlined, PlusOutlined } from "@ant-design/icons";

import Alert from "/src/util/alert";

import EntitySelect from "../../component/EntitySelect";
import { actionsColumn, dash, initialsOf } from "../../component/table-cells";
import { COUNTERPARTIES_PAGE, TRANSLATION_DOWNLOADER } from "../../config/lang";
import { call, getAbortable, parseErrors } from "../../util/action";
import { translate } from "../../util/util";

/* The endpoint lists live types only — the API refuses a deleted one for a NEW link. One that is
   already on a link still shows in the table, as it came. */
const LINK_TYPE_QUERY = { kind: "counterparty_link_type" };
const FIELDS = ["counterparty", "link_type"];
const FIELD_ID = { counterparty: "cp-link-counterparty", link_type: "cp-link-type" };

const idOf = v => (v && typeof v === "object" ? v.id ?? v.value : v);

export default function LinksSection({ counterpartyId, links, lang, onChange, linkTo, onOpen }) {
  const t = key => translate(COUNTERPARTIES_PAGE, key, lang);
  const cancelText = translate(TRANSLATION_DOWNLOADER, "cancel", lang);
  const toast = { position: "top-right" };
  const base = `excalibur/counterparties/${counterpartyId}/links/`;

  const [open, setOpen] = useState(false);
  const [picked, setPicked] = useState(null);
  const [linkType, setLinkType] = useState(null);
  const [errors, setErrors] = useState({});
  const [busy, setBusy] = useState(false);

  /* Re-read the card and keep ONLY its links — after a link turned out to be gone, or a failed
     write left the list uncertain. A full reload would reset an open form. */
  const refresh = () =>
    getAbortable(`excalibur/counterparties/${counterpartyId}/`)
      .then(response => response?.id && onChange(response.linked_counterparties || []))
      .catch(() => {});

  const close = () => {
    setOpen(false);
    setPicked(null);
    setLinkType(null);
    setErrors({});
  };

  const pick = (field, setter) => value => {
    setter(value);
    setErrors(prev => (prev[field] ? { ...prev, [field]: false } : prev));
  };

  const submit = () => {
    setBusy(true);
    setErrors({});
    call({ method: "POST", url: base, post: { counterparty: idOf(picked), link_type: linkType ? idOf(linkType) : null } }, response => {
      setBusy(false);
      if (response?.id) {
        onChange(response.linked_counterparties || []);
        close();
        Alert.success(t("links_added_msg"), toast);
        return;
      }
      /* A 400 is the raw `Response`: mark the refused field(s) with the form's one phrase — the
         API's English is never shown — and toast anything that is not one of the two fields. A
         5xx / network failure has already been toasted by `action.js`. */
      if (typeof response?.json === "function") {
        parseErrors(response).then(parsed => {
          const keys = Object.keys(parsed.fields);
          const known = keys.filter(key => FIELDS.includes(key));
          if (known.length) {
            setErrors(Object.fromEntries(known.map(key => [key, true])));
            setTimeout(() => document.getElementById(FIELD_ID[known[0]])?.focus(), 0);
          }
          if (!known.length || known.length < keys.length) Alert.warning(t("edit_error_msg"), toast);
        });
        return;
      }
      if (!response?.failed) Alert.warning(t("edit_error_msg"), toast);
    });
  };

  /* Destructive, so confirmed — naming the counterparty, with the verb on the button (spec/03). */
  const unlink = row =>
    Modal.confirm({
      title: t("links_unlink_confirm").replace("{name}", row.name),
      okText: t("links_unlink"),
      okButtonProps: { danger: true },
      /* Static `Modal.confirm` renders outside the tree and does not inherit AppShell's
         `ConfigProvider locale` — both labels are passed or Cancel stays English. */
      cancelText,
      onOk: () =>
        new Promise(resolve =>
          call({ method: "DELETE", url: `${base}${row.id}/` }, response => {
            resolve();
            if (response?.id) {
              onChange(response.linked_counterparties || []);
              Alert.success(t("links_removed_msg"), toast);
              return;
            }
            /* 404 `{detail}`: someone else unlinked it first. Say so and show what is there now. */
            if (response?.detail) Alert.warning(t("links_gone_msg"), toast);
            refresh();
          })
        ),
    });

  const fieldError = field => (errors[field] ? { validateStatus: "error", help: t("field_error") } : {});

  const columns = [
    {
      key: "name",
      title: t("links_field_counterparty"),
      render: (value, row) => {
        const sub = [row.department?.name, row.client_type?.label].filter(Boolean).join(" · ");
        return (
          <Flex align="center" gap={12} className="cp-links__who">
            <Avatar shape="square" size={30} className="cp-logo" style={{ fontSize: 12 }}>
              {initialsOf(row.name)}
            </Avatar>
            <div className="cp-links__text">
              {/* A real link — it opens in a new tab like any other — carrying the list's filters
                  on in route state, as the list's own rows do. `onOpen` asks first when the form
                  holds unsaved edits. */}
              <Link className="cp-links__name" to={linkTo(row.id)} title={row.name} onClick={event => onOpen(event, row.id)}>
                {row.name}
              </Link>
              {sub ? (
                <span className="cp-links__sub" title={sub}>
                  {sub}
                </span>
              ) : null}
            </div>
          </Flex>
        );
      },
    },
    {
      /* A neutral tag — the category's in the list, not a stage colour: a link type is a plain
         dictionary value. A retired type still shows, as the API sends it. */
      key: "link_type",
      title: t("links_field_type"),
      width: 200,
      render: (value, row) =>
        row.link_type?.label ? (
          <Tag className="cp-cat">
            <Typography.Text ellipsis={{ tooltip: row.link_type.label }}>{row.link_type.label}</Typography.Text>
          </Tag>
        ) : (
          dash
        ),
    },
    /* One action, so one button — the column and its width are still `actionsColumn`'s, so the
       right edge lines up with every other table's row-action slot. The name of the counterparty
       is in the accessible name: a column of identical "Unlink" buttons says nothing to a screen
       reader on its own. */
    actionsColumn((value, row) => (
      <Tooltip title={t("links_unlink")}>
        <Button size="small" icon={<DisconnectOutlined />} aria-label={`${t("links_unlink")}: ${row.name}`} onClick={() => unlink(row)} />
      </Tooltip>
    )),
  ];

  return (
    <section className="cp-links" aria-label={t("links_title")}>
      <Flex className="cp-links__head" align="center" justify="space-between" gap="small" wrap>
        <Typography.Text strong className="cp-links__title">
          {t("links_title")}
        </Typography.Text>
        <Button icon={<PlusOutlined />} onClick={() => setOpen(true)}>
          {t("links_add")}
        </Button>
      </Flex>

      <Table
        className="lp-table__grid"
        rowKey="id"
        size="middle"
        columns={columns}
        dataSource={links}
        pagination={false}
        /* Scrolls inside itself on a phone (spec/02), so the page never does. */
        scroll={{ x: 520 }}
        locale={{ emptyText: <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={t("links_empty")} /> }}
      />

      <Modal
        open={open}
        title={t("links_modal_title")}
        okText={t("links_add")}
        okButtonProps={{ disabled: !picked, loading: busy }}
        cancelText={cancelText}
        onOk={submit}
        onCancel={close}
        destroyOnHidden>
        <Form component={false} layout="vertical" colon={false}>
          <Flex vertical gap="small" className="dp-form">
            <Form.Item label={t("links_field_counterparty")} htmlFor={FIELD_ID.counterparty} {...fieldError("counterparty")}>
              {/* The short counterparty list, searched by name. It offers everything in the
                  company, this card and the ones already linked included: the API refuses those,
                  and its 400 marks this field. */}
              <EntitySelect
                id={FIELD_ID.counterparty}
                entity="counterparty"
                value={picked?.id}
                seed={picked}
                placeholder={t("links_field_counterparty")}
                onChange={pick("counterparty", setPicked)}
              />
            </Form.Item>
            <Form.Item label={t("links_field_type")} htmlFor={FIELD_ID.link_type} {...fieldError("link_type")}>
              <EntitySelect
                id={FIELD_ID.link_type}
                entity="counterparty_link_type"
                extraQuery={LINK_TYPE_QUERY}
                value={linkType?.id}
                seed={linkType}
                placeholder={t("links_type_placeholder")}
                onChange={pick("link_type", setLinkType)}
              />
            </Form.Item>
          </Flex>
        </Form>
      </Modal>
    </section>
  );
}
